import { Component, Input } from '@angular/core';
import { LanguageService } from '../../services/language.service';
import { EventDoc } from '../../models/event.model';
import { eventDatePart } from '../event-utils';

/**
 * The stacked date chip on the featured card — iOS `EventCardView`'s month / day / weekday block.
 *
 * A coloured month strip over a white body carrying the day and weekday. Each line comes from
 * `eventDatePart`, which joins a range with " - " only in the components that actually differ, so a
 * 22-28 April event reads "APR" over "22 - 28" rather than repeating the month.
 *
 * The chip is white with dark text on every surface (iOS parity), like the price pill.
 *
 * Reads the language through a getter rather than subscribing: default change detection re-evaluates
 * it, so a language switch re-renders the badge with no subscription to leak.
 */
@Component({
    selector: 'app-event-date-badge',
    template: `
    <div class="ev-date">
      <div class="ev-date-month">{{ month }}</div>
      <div class="ev-date-body">
        <div class="ev-date-day">{{ day }}</div>
        <div class="ev-date-weekday">{{ weekday }}</div>
      </div>
    </div>
  `,
    styles: [`
    :host { display: inline-flex; }
    .ev-date {
      display: flex;
      flex-direction: column;
      min-width: 45px;
      border-radius: 5px;
      overflow: hidden;
      text-align: center;
      background: #FFFFFF;
    }
    .ev-date-month {
      background: var(--ev-date-badge, #FF6064);
      color: var(--ev-on-date-badge, #0C0C0C);
      font-size: 9px;
      font-weight: 600;
      text-transform: uppercase;
      padding: 2px;
      letter-spacing: 0.02em;
    }
    .ev-date-body {
      padding: 2px 4px 3px;
    }
    .ev-date-day {
      color: #3D3D3D;
      font-size: 14px;
      font-weight: 700;
      line-height: 1.15;
    }
    .ev-date-weekday {
      color: #7A7A7A;
      font-size: 11px;
      text-transform: uppercase;
      line-height: 1.15;
    }
  `],
    standalone: false
})
export class EventDateBadgeComponent {
  @Input() event!: EventDoc;

  constructor(private languageService: LanguageService) {}

  get day(): string {
    return eventDatePart(this.event, 'day', this.languageService.getCurrentLang());
  }

  get month(): string {
    return eventDatePart(this.event, 'month', this.languageService.getCurrentLang());
  }

  get weekday(): string {
    return eventDatePart(this.event, 'weekday', this.languageService.getCurrentLang());
  }
}
